/**
 * Calendar-day keys, read in a named IANA time zone.
 *
 * A household's day is the same on every device only if the zone is named
 * rather than taken from whichever device is asking, so every helper here takes
 * a zone and defaults to the device's own.
 */
export type DateKey = string; // YYYY-MM-DD

/** An IANA time zone name, such as `Europe/London`. */
export type TimeZoneName = string;

export function deviceTimeZone(): TimeZoneName {
  return new Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const formatters = new Map<string, Intl.DateTimeFormat>();

function formatterFor(timeZone: TimeZoneName): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', {
      timeZone,
      calendar: 'gregory',
      numberingSystem: 'latn',
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

export interface ZonedParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
}

/** The wall-clock reading of an instant in a zone. */
export function zonedParts(date: Date, timeZone: TimeZoneName = deviceTimeZone()): ZonedParts {
  const out: Record<string, number> = {};
  for (const part of formatterFor(timeZone).formatToParts(date)) {
    if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  return {
    year: out.year ?? 1970,
    month: out.month ?? 1,
    day: out.day ?? 1,
    hour: (out.hour ?? 0) % 24,
    minute: out.minute ?? 0,
  };
}

const pad = (n: number, width = 2) => `${n}`.padStart(width, '0');

export function toDateKey(d: Date, timeZone: TimeZoneName = deviceTimeZone()): DateKey {
  const p = zonedParts(d, timeZone);
  return `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}`;
}

export function todayKey(
  now: Date = new Date(),
  timeZone: TimeZoneName = deviceTimeZone(),
): DateKey {
  return toDateKey(now, timeZone);
}

/** Minutes since midnight on the wall clock of a zone. */
export function minutesOfDay(now: Date, timeZone: TimeZoneName = deviceTimeZone()): number {
  const p = zonedParts(now, timeZone);
  return p.hour * 60 + p.minute;
}

/** The year, month (1-12) and day a key names, with no zone involved. */
export function keyParts(key: DateKey): { year: number; month: number; day: number } {
  const [y, m, d] = key.split('-').map(Number);
  return { year: y ?? 1970, month: m ?? 1, day: d ?? 1 };
}

/** Midnight UTC of the key's date: calendar arithmetic that no clock change can touch. */
function utcOf(key: DateKey): Date {
  const { year, month, day } = keyParts(key);
  return new Date(Date.UTC(year, month - 1, day));
}

function keyOfUtc(d: Date): DateKey {
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/**
 * A well-formed key that names the day it says it does.
 *
 * `fromDateKey` is deliberately lax — it rolls `2026-02-30` over into March
 * rather than failing — which is right for a key the app wrote itself and
 * wrong for one that arrived in a URL. The round trip is what catches it.
 */
export function isDateKey(value: string | null | undefined): value is DateKey {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  return keyOfUtc(utcOf(value)) === value;
}

/**
 * A day a record can be written to: real, and not still to come.
 *
 * Keys sort lexicographically, so the whole of the future check is a string
 * comparison. `today` is the prayer-day, which before Fajr is yesterday's key.
 */
export function isRecordableDay(
  value: string | null | undefined,
  today: DateKey,
): value is DateKey {
  return isDateKey(value) && value <= today;
}

/** Local midnight of the key's date on this device. For calendar use only. */
export function fromDateKey(key: DateKey): Date {
  const { year, month, day } = keyParts(key);
  return new Date(year, month - 1, day);
}

/** Add days to a key. Pure calendar arithmetic: a clock change never skips or repeats a day. */
export function addDays(key: DateKey, delta: number): DateKey {
  const d = utcOf(key);
  d.setUTCDate(d.getUTCDate() + delta);
  return keyOfUtc(d);
}

/** Inclusive list of day keys, oldest first. */
export function dateRange(from: DateKey, to: DateKey): DateKey[] {
  const out: DateKey[] = [];
  for (let k = from; k <= to; k = addDays(k, 1)) out.push(k);
  return out;
}
